import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { YoklamaSistemiError } from "../types/ErrorModals/YoklamaSistemiError";
import { YoklamaSistemiResponseModal } from "../types/modals";
import {
  getYoklamaSistemi,
  kaydetYoklamaSistemi,
} from "../services/api/yoklamaSistemi";

export const useYoklamaSistemi = (
  kurumKodu: string,
  ogretmenId: number,
  sezon: number
) => {
  // Local states
  const [selectedDurum, setSelectedDurum] = useState<
    Record<number, Record<number, string>>
  >({});
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>(
    {}
  );
  const [kayitYapiliyor, setKayitYapiliyor] = useState(false);

  // Veri çekme
  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<YoklamaSistemiResponseModal[], YoklamaSistemiError>({
    queryKey: ["yoklamaSistemi", kurumKodu, ogretmenId],
    queryFn: async () => {
      try {
        const response = await getYoklamaSistemi({
          kurumKodu,
          ogretmenId,
          sezon,
        });
        return response;
      } catch (error) {
        throw error as YoklamaSistemiError;
      }
    },
    enabled: Boolean(kurumKodu && ogretmenId),
    retry: 1,
  });

  // Data değiştiğinde initial state'i oluştur
  useEffect(() => {
    if (data) {
      const initialState: Record<number, Record<number, string>> = {};

      data.forEach((ders) => {
        const dersDurumlari: Record<number, string> = {};

        ders.ogrenciDurumListesi.forEach((ogrenci) => {
          if (ogrenci.dev && ogrenci.dev.trim() !== "") {
            dersDurumlari[ogrenci.ogrenciId] = ogrenci.dev;
          }
        });

        if (Object.keys(dersDurumlari).length > 0) {
          initialState[ders.id] = dersDurumlari;
        }
      });

      setSelectedDurum(initialState);
    }
  }, [data]);

  // Handlers
  const handleDurumChange = (
    dersId: number,
    ogrenciId: number,
    durum: string
  ) => {
    setSelectedDurum((prev) => {
      if (!durum) {
        const dersDurumlari = { ...(prev[dersId] || {}) };
        delete dersDurumlari[ogrenciId];

        if (Object.keys(dersDurumlari).length === 0) {
          const newState = { ...prev };
          delete newState[dersId];
          return newState;
        }

        return {
          ...prev,
          [dersId]: dersDurumlari,
        };
      }

      return {
        ...prev,
        [dersId]: {
          ...(prev[dersId] || {}),
          [ogrenciId]: durum,
        },
      };
    });
  };

  const toggleCard = (dersId: number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [dersId]: !prev[dersId],
    }));
  };

  const handleYoklamaAl = async (
    dersId: number,
    hocaId: number,
    sinifId: number,
    kurumKodu: string,
    saatNo: number,
    sezon: number
  ) => {
    try {
      setKayitYapiliyor(true);

      await kaydetYoklamaSistemi({
        dersId,
        hocaId,
        sinifId,
        kurumKodu,
        sezon,
        yoklamalar: Object.entries(selectedDurum[dersId] || {}).map(
          ([ogrenciId, durum]) => ({
            ogrenciId: Number(ogrenciId),
            durum,
            saatNo,
          })
        ),
      });

      setSelectedDurum((prev) => {
        const newState = { ...prev };
        delete newState[dersId];
        return newState;
      });

      await refetch();
    } catch (error) {
      console.error("Yoklama kaydedilirken hata:", error);
      throw error;
    } finally {
      setKayitYapiliyor(false);
    }
  };

  return {
    data: data || [],
    loading,
    error: error || null,
    selectedDurum,
    expandedCards,
    kayitYapiliyor,
    handleDurumChange,
    toggleCard,
    handleYoklamaAl,
    refetch,
  };
};
